import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { GlobalContext } from "../../Context/GlobalContext";
import HealthDetailDrawer from "../../Components/HealthDetailDrawer";
import { Info, ListTodo } from "lucide-react";
import { normalizeScanDetails } from "./scanNormalizer";
import { useLocation, useNavigate } from "react-router-dom";
import {
  statusIconMap,
  styleKeyFromCategory,
  getCategoryStyle,
  getWellnessMessage,
  getWellnessLabel,
} from "../../utils";

const UserScanDetails = () => {
  const {
    tempScanDetails,
    translatedLabels,
    getScanDetails,
    showExportModal,
    setShowExportModal,
    wellnessLabel,
    is4Life,
  } = useContext(GlobalContext);

  const location = useLocation();
  const selectedDate = location?.state?.uniqueKey || "";
  const chartRef = useRef(null);
  const screenLabels = translatedLabels?.common;
  const navigate = useNavigate();

  const [scanDetails, setScanDetails] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [showCardDetail, setShowCardDetail] = useState(false);
  const [showIconAndImage, setShowIconAndImage] = useState(false);
  const [selectedMetricData, setSelectedMetricData] = useState(null);

  useEffect(() => {
    if (tempScanDetails) {
      const normalized = normalizeScanDetails(tempScanDetails, screenLabels);
      setScanDetails(normalized);
    }
  }, [tempScanDetails, screenLabels]);

  useEffect(() => {
    setScanDetails([]);
    getScanDetails(selectedDate);
  }, [getScanDetails, selectedDate]);

  const wellnessMetric = useMemo(() => {
    const wellnessCategory = scanDetails.find((c) => c.category === "Wellness");
    return wellnessCategory?.metrics?.find((m) => m.key === "wellness");
  }, [scanDetails]);

  let wellnessValue = wellnessMetric?.value ? wellnessMetric.value : "";

  const options = {
    chart: { type: "radialBar", offsetY: 0, sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        max: 100,
        startAngle: -150,
        endAngle: 150,
        hollow: { size: "85%", background: "transparent" },
        track: { background: "#f0f0f0", strokeWidth: "100%", margin: 0 },
        dataLabels: {
          name: {
            offsetY: 30,
            show: true,
            color: "#574f8a",
            fontSize: "16px",
          },
          value: {
            offsetY: -10,
            fontSize: "29px",
            color: "#574f8a",
            fontWeight: 600,
            show: true,
            formatter: () => (wellnessValue ? `${wellnessValue}/10` : "0/10"),
          },
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.4,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 25, 50, 75, 100],
        colorStops: [
          { offset: 0, color: "#00d4ff", opacity: 1 },
          { offset: 25, color: "#7c3aed", opacity: 1 },
          { offset: 50, color: "#ec4899", opacity: 1 },
          { offset: 75, color: "#f97316", opacity: 1 },
          { offset: 100, color: "#ef4444", opacity: 1 },
        ],
      },
    },
    stroke: { lineCap: "round" },
    labels: [getWellnessLabel(wellnessValue, screenLabels)],
  };

  const series = [
    wellnessMetric?.value ? (Number(wellnessMetric.value) / 10) * 100 : 0,
  ];

  const healthCards = useMemo(() => {
    if (!scanDetails || scanDetails.length === 0) return [];
    const cards = [];
    scanDetails.forEach((category) => {
      if (category.category === "Wellness") return;

      category.metrics.forEach((metric) => {
        if (!metric.value) return;
        cards.push({
          id: `${category.category}-${metric.key}`,
          key: category.category,
          title: metric.label,
          category: metric.label,
          categoryScore: metric.value,
          avgReadingScore: metric.avg || "-",
          rawMetric: metric,
        });
      });

      if (
        category.category === "Stress" &&
        Array.isArray(tempScanDetails?.stress?.rri_data) &&
        tempScanDetails.stress.rri_data.length > 0
      ) {
        cards.push({
          id: `stress-rri-chart`,
          key: "Stress",
          title: screenLabels?.rr_intervals?.label_value || "RRI Trend",
          isRRIChart: true,
          rawMetric: {
            key: "rri_data",
            value: tempScanDetails.stress.rri_data,
            comment: "RRI intervals data used for HRV analysis",
          },
        });
      }
    });

    return cards;
  }, [scanDetails, tempScanDetails?.stress?.rri_data, screenLabels]);

  const cardOrder = ["Heart Health", "Blood", "Stress", "Body", "Risks"];
  const customFilterOrder = [
    "all",
    "Heart Health",
    "Blood",
    "Stress",
    "Body",
    "Risks",
  ];

  useEffect(() => {
    if (showExportModal) {
      navigate("/view-export", { state: { scanID: selectedDate } });
      setShowExportModal(false);
    }
  }, [showExportModal, navigate, selectedDate]);

  const openDrawer = (metricData, withIcon = false) => {
    setSelectedMetricData(metricData);
    setShowIconAndImage(withIcon);
    setShowCardDetail(true);

    window.history.pushState({ modalOpen: true }, "");
  };

  const closeDrawer = () => {
    setShowCardDetail(false);
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      if (showCardDetail) {
        setShowCardDetail(false);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [showCardDetail]);

  return (
    <>
      <div className="content-part mt-2">
        {wellnessMetric?.value && (
          <div
            className="chart-wrapper d-flex justify-content-center"
            ref={chartRef}
            style={{ marginBottom: "1rem" }}
          >
            <div className="text-center">
              <Chart
                options={options}
                series={series}
                type="radialBar"
                height={170}
              />
              <div className="d-flex justify-content-center align-items-center flex-column">
                <p
                  className="wellness-title mb-0 d-flex align-items-center gap-1"
                // onClick={() =>
                //   openDrawer(
                //     {
                //       ...wellnessMetric,
                //       unit: "/10",
                //       icon: statusIconMap[wellnessMetric?.status],
                //     },
                //     false,
                //   )
                // }
                >
                  {screenLabels?.wellness_score?.label_value ||
                    "Wellness Score"}{" "}
                  {/* <Info className="text-primary" size={20} /> */}
                </p>
                <p className="wellness-desc">{wellnessMetric?.comment}</p>
              </div>
            </div>
          </div>
        )}

        {wellnessMetric?.value && (
          <div className="my-3">
            <h6>
              {screenLabels?.wellness_insight?.label_value ||
                "Wellness Insights"}
            </h6>
            <div className="wellness-insights-card shadow-sm">
              <p className="mb-0 small">
                {getWellnessMessage(wellnessMetric?.value, screenLabels)}
              </p>
            </div>
          </div>
        )}

        <h6>
          {screenLabels?.health_biomarkers?.label_value || "Health Biomarkers"}
        </h6>
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex align-items-center gap-1 overflow-auto hide-scrollbar">
              {customFilterOrder.map((key) => {
                const normalizedKey = key.toLowerCase().replace(/\s+/g, "_");
                // if (["blood", "stress", "risks"].includes(normalizedKey)) return null; // temp hide
                const label =
                  key === "all"
                    ? screenLabels?.all?.label_value || "All"
                    : screenLabels?.[normalizedKey]?.label_value ||
                    key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase());

                if (key === "all") {
                  return (
                    <div className="filter-item text-center" key={key}>
                      <button
                        className={`filter-btn ${activeTab === "all" ? "active" : ""}`}
                        onClick={() => setActiveTab("all")}
                        style={{
                          border:
                            activeTab === "all"
                              ? "2px solid var(--primaryColor)"
                              : "",
                        }}
                      >
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          <ListTodo />
                        </span>
                      </button>
                      <span className="mb-0 pb-0" title={label}>
                        {label}
                      </span>
                    </div>
                  );
                }

                const { icon, color } = getCategoryStyle(key);
                return (
                  <div className="filter-item text-center" key={key}>
                    <button
                      className={`filter-btn ${color} ${activeTab === key ? "active" : ""}`}
                      onClick={() => setActiveTab(key)}
                    >
                      <img
                        src={icon}
                        alt={label}
                        className="filter-icon"
                        style={{ width: "24px", height: "24px" }}
                      />
                    </button>
                    <span className="mb-0 pb-0" title={label}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <section>
          <h6 className="mb-3">
            {screenLabels?.latest_indicators?.label_value ||
              "Health Indicators"}
          </h6>

          {/* Group cards by category */}
          {Object.entries(
            healthCards
              .filter((card) => {
                if (activeTab === "all") return true;
                return card.key === activeTab;
              })
              .sort((a, b) => {
                const indexA = cardOrder.indexOf(a.key);
                const indexB = cardOrder.indexOf(b.key);
                return (
                  (indexA === -1 ? 999 : indexA) -
                  (indexB === -1 ? 999 : indexB)
                );
              })
              .reduce((acc, card) => {
                if (!acc[card.key]) acc[card.key] = [];
                acc[card.key].push(card);
                return acc;
              }, {}),
          ).map(([category, cards]) => {
            const { icon, color } = getCategoryStyle(category);

            return (
              <div key={category} className="mb-4">
                <div
                  className={`d-flex flex-row justify-content-center gap-2 align-items-center mt-0 mb-3 rounded ${color}`}
                  style={{
                    position: "sticky",
                    top: "-8px",
                    zIndex: 2,
                    boxShadow: "0 -14px 0 var(--pageBgColor)",
                  }}
                >
                  <div>
                    <img
                      src={icon}
                      alt={category}
                      style={{ width: "24px", height: "24px" }}
                    />
                  </div>
                  <h5 className="mt-2 text-center">
                    {screenLabels?.[category.toLowerCase().replace(/\s+/g, "_")]
                      ?.label_value || category}
                  </h5>
                </div>

                {cards.map((card) =>
                  card.isRRIChart ? (
                    <div
                      key={card.id}
                      className={`card ${is4Life ? `fourlife-shadow-stress` : `shadow-stress`
                        } d-flex flex-column px-2 py-2 mb-3`}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        openDrawer(
                          {
                            key: card.rawMetric.key,
                            value: card.rawMetric.value,
                            unit: "ms",
                            status: "Info",
                            comment: card.rawMetric.comment,
                          },
                          false,
                        )
                      }
                    >
                      <div className="scan-result-header d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                          <div>
                            <h6 className="mt-auto mb-0">{card.title}</h6>
                            <p className="mb-0 fs-13px">
                              {screenLabels?.rr_intervals?.label_value ||
                                "Heart rate trend from R-R intervals"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2">
                        <Chart
                          type="line"
                          height={180}
                          options={{
                            chart: {
                              id: "rri-heart-rate-chart",
                              toolbar: { show: false },
                              zoom: { enabled: false },
                            },
                            stroke: { curve: "smooth", width: 2 },
                            markers: { size: 0 },
                            xaxis: {
                              title: {
                                text:
                                  screenLabels?.time_s?.label_value ||
                                  "Time (s)",
                              },
                              labels: {
                                formatter: (val) => Number(val).toFixed(1),
                              },
                            },
                            yaxis: {
                              title: {
                                text:
                                  screenLabels?.heart_rate_bpm?.label_value ||
                                  "Heart Rate (BPM)",
                              },
                              min: 40,
                              max: 140,
                            },
                            tooltip: {
                              x: {
                                formatter: (val) =>
                                  `${Number(val).toFixed(2)}s`,
                              },
                              y: {
                                formatter: (val) =>
                                  `${Number(val).toFixed(2)} BPM`,
                              },
                            },
                          }}
                          series={[
                            {
                              name:
                                screenLabels?.heart_rate?.label_value ||
                                "Heart Rate",
                              data: Array.isArray(card.rawMetric.value)
                                ? card.rawMetric.value
                                  .map((entry) => {
                                    if (
                                      entry &&
                                      typeof entry === "object" &&
                                      "timestamp" in entry &&
                                      "interval" in entry
                                    ) {
                                      const bpm =
                                        Math.round(
                                          (60000 / entry.interval) * 100,
                                        ) / 100;
                                      return [Number(entry.timestamp), bpm];
                                    }
                                    return null;
                                  })
                                  .filter(Boolean)
                                : [],
                            },
                          ]}
                        />
                      </div>

                      <div className="info-icon">
                        <Info size={18} />
                      </div>
                    </div>
                  ) : (
                    <div
                      key={card.id}
                      className={`card ${is4Life
                          ? `fourlife-shadow-${styleKeyFromCategory(card.key)}`
                          : `shadow-${styleKeyFromCategory(card.key)}`
                        } d-flex flex-row justify-content-between px-2 py-2 mb-3`}
                      onClick={() =>
                        openDrawer(
                          {
                            ...card.rawMetric,
                            icon:
                              statusIconMap[card.rawMetric?.status] ||
                              statusIconMap["normal"],
                          },
                          true,
                        )
                      }
                    >
                      <div
                        className={`icon-wrapper ${getCategoryStyle(card.key).color}`}
                      >
                        <img
                          src={getCategoryStyle(card.key).icon}
                          alt={card.category}
                          style={{ width: "24px", height: "24px" }}
                        />
                      </div>

                      <div className="flex-grow-1 px-2">
                        <h6 className="mb-0 fw-semibold welless-card-title">
                          {card.title}
                        </h6>

                        <div className="d-flex justify-content-between bp-details">
                          <div>
                            <p className="mb-0 font-bold fs-14px">
                              {card.rawMetric?.key === "heart_age"
                                ? screenLabels?.cronological_age?.label_value ||
                                "Chronological Age"
                                : screenLabels?.reading?.label_value ||
                                "Current Reading"}
                            </p>
                            <p className="mb-0 fs-14px fw-semibold">
                              {card.rawMetric?.key === "heart_age"
                                ? card.rawMetric.cronologicalAge || "-"
                                : card.categoryScore}
                            </p>
                          </div>

                          <div>
                            <p className="mb-0 fs-14px text-end">
                              {card.rawMetric?.key === "heart_age"
                                ? screenLabels?.biological_age?.label_value ||
                                "Biological Age"
                                : wellnessLabel || "-"}
                            </p>
                            <p className="mb-0 fs-14px fw-semibold">
                              {card.rawMetric?.key === "heart_age"
                                ? card.categoryScore
                                : card.avgReadingScore || "-"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="info-icon me-1">
                        <Info size={18} />
                      </div>
                    </div>
                  ),
                )}
              </div>
            );
          })}
        </section>

        <HealthDetailDrawer
          show={showCardDetail}
          onClose={closeDrawer}
          activeTab={activeTab}
          showIconAndImage={showIconAndImage}
          metricData={selectedMetricData}
        />
      </div>
    </>
  );
};

export default UserScanDetails;
